import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

// Global manual-refresh window: after the user clicks the top-bar Refresh, force cached GET
// endpoints to rebuild (skip server cache) by adding ?refresh=1 for a short window. Non-cached
// endpoints simply ignore the extra query param.
let _forceRefreshUntil = 0;
export function markForceRefresh(ms = 6000) { _forceRefreshUntil = Date.now() + ms; }

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("jde_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if ((config.method || "get").toLowerCase() === "get" && Date.now() < _forceRefreshUntil) {
    config.params = { ...(config.params || {}), refresh: 1 };
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    const url = err.config?.url || "";
    // Only a genuine authentication failure (401) on a request that carried a token
    // forces a logout. 403 (authorization) and transient/network errors must NOT
    // destroy the session. The auth endpoints handle their own failures.
    const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/me") || url.includes("/auth/set-password");
    const hadToken = !!localStorage.getItem("jde_token");
    if (status === 401 && hadToken && !isAuthEndpoint) {
      localStorage.removeItem("jde_token");
      if (window.location.pathname !== "/login") {
        window.location.assign("/login");
      }
    }
    return Promise.reject(err);
  }
);

export default api;

/**
 * Start an O365 mailbox poll and wait for it to finish by polling the status endpoint.
 * The poll runs in the BACKGROUND on the server so a large email backlog can't exceed the
 * ~120s edge/proxy timeout. Resolves with the final result
 * {fetched, processed, skipped_already_ingested, recovered_carting}; rejects on poll error.
 * @param {number} timeoutMs - max time to wait before giving up (default 5 min)
 */
export async function pollO365WithProgress(timeoutMs = 300000) {
  await api.post("/emails/poll-o365"); // idempotent: no-op if one is already running
  const deadline = Date.now() + timeoutMs;
  // small initial delay before first status check
  await new Promise((r) => setTimeout(r, 1500));
  while (Date.now() < deadline) {
    const { data } = await api.get("/emails/poll-o365/status");
    if (data && data.running === false) {
      if (data.error) throw new Error(data.error);
      return data;
    }
    await new Promise((r) => setTimeout(r, 3000));
  }
  throw new Error("Still polling — this is taking a while. Check Intake Review shortly.");
}
